/* eslint-disable react-refresh/only-export-components */
import ClearIcon from "@mui/icons-material/Clear";
import DragIndicator from "@mui/icons-material/DragIndicator";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RemoveIcon from "@mui/icons-material/Remove";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  Paper,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { type DragEvent, useCallback, useMemo, useState } from "react";

export type ScoutDashboardChartOption = {
  id: string;
  title: string;
};

export type ScoutDashboardSectionOption = {
  id: string;
  title: string;
  charts: ScoutDashboardChartOption[];
};

export type ScoutDashboardSectionConfig = {
  sectionOrder: string[];
  hiddenSectionIds: string[];
  chartOrder?: undefined;
  hiddenChartIds?: undefined;
};

export type LegacyScoutDashboardChartConfig = {
  sectionOrder?: undefined;
  hiddenSectionIds?: undefined;
  chartOrder: string[];
  hiddenChartIds: string[];
};

export type ScoutDashboardSectionSettings = {
  current: ScoutDashboardSectionConfig | LegacyScoutDashboardChartConfig;
  savedConfigs: Record<string, ScoutDashboardSectionConfig | LegacyScoutDashboardChartConfig>;
  activeConfigName: string | null;
};

export type ScoutDashboardNormalizedSectionSettings = {
  current: ScoutDashboardSectionConfig;
  savedConfigs: Record<string, ScoutDashboardSectionConfig>;
  activeConfigName: string | null;
};

export const DEFAULT_SCOUT_DASHBOARD_SECTION_SETTINGS: ScoutDashboardSectionSettings = {
  current: {
    sectionOrder: [],
    hiddenSectionIds: [],
  },
  savedConfigs: {},
  activeConfigName: null,
};

export function getScoutDashboardChartStorageKey(userEmail?: string | null): string {
  return `scout-dashboard-chart-settings|${userEmail ?? "anonymous"}`;
}

export function createDefaultScoutDashboardSectionConfig(
  sectionIds: string[]
): ScoutDashboardSectionConfig {
  return {
    sectionOrder: [...sectionIds],
    hiddenSectionIds: [],
  };
}

function getSectionIds(sections: ScoutDashboardSectionOption[]): string[] {
  return sections.map((section) => section.id);
}

function buildChartIdToSectionId(sections: ScoutDashboardSectionOption[]): Map<string, string> {
  const chartIdToSectionId = new Map<string, string>();

  for (const section of sections) {
    for (const chart of section.charts) {
      chartIdToSectionId.set(chart.id, section.id);
    }
  }

  return chartIdToSectionId;
}

function normalizeSectionOrder(input: {
  sectionIds: string[];
  preferredOrder: string[];
}): string[] {
  const sectionIdSet = new Set(input.sectionIds);
  const seenOrderedIds = new Set<string>();
  const sectionOrder: string[] = [];

  for (const sectionId of input.preferredOrder) {
    if (sectionIdSet.has(sectionId) && !seenOrderedIds.has(sectionId)) {
      sectionOrder.push(sectionId);
      seenOrderedIds.add(sectionId);
    }
  }

  for (const sectionId of input.sectionIds) {
    if (!seenOrderedIds.has(sectionId)) {
      sectionOrder.push(sectionId);
      seenOrderedIds.add(sectionId);
    }
  }

  return sectionOrder;
}

function migrateLegacyChartConfig(
  config: LegacyScoutDashboardChartConfig,
  sections: ScoutDashboardSectionOption[]
): ScoutDashboardSectionConfig {
  const sectionIds = getSectionIds(sections);
  const chartIdToSectionId = buildChartIdToSectionId(sections);
  const preferredSectionOrder: string[] = [];
  const seenSectionIds = new Set<string>();

  for (const chartId of config.chartOrder) {
    const sectionId = chartIdToSectionId.get(chartId);
    if (sectionId && !seenSectionIds.has(sectionId)) {
      preferredSectionOrder.push(sectionId);
      seenSectionIds.add(sectionId);
    }
  }

  const hiddenChartIdSet = new Set(config.hiddenChartIds);
  const hiddenSectionIds = sections
    .filter(
      (section) =>
        section.charts.length > 0 && section.charts.every((chart) => hiddenChartIdSet.has(chart.id))
    )
    .map((section) => section.id);

  return {
    sectionOrder: normalizeSectionOrder({
      sectionIds,
      preferredOrder: preferredSectionOrder,
    }),
    hiddenSectionIds,
  };
}

/**
 * Reconciles a stored section configuration with the sections currently returned
 * by the dashboard query. Legacy chart-level configs are migrated by deriving the
 * section order from the first chart encountered in each section, and only hiding
 * a section when all of its charts were hidden.
 */
export function normalizeScoutDashboardSectionConfig(
  config: ScoutDashboardSectionConfig | LegacyScoutDashboardChartConfig | undefined,
  sections: ScoutDashboardSectionOption[]
): ScoutDashboardSectionConfig {
  const sectionIds = getSectionIds(sections);

  if (config?.chartOrder) {
    return migrateLegacyChartConfig(config, sections);
  }

  const sectionOrder = normalizeSectionOrder({
    sectionIds,
    preferredOrder: config?.sectionOrder ?? [],
  });
  const hiddenSectionIdSet = new Set(config?.hiddenSectionIds ?? []);
  const hiddenSectionIds = sectionIds.filter((sectionId) => hiddenSectionIdSet.has(sectionId));

  return {
    sectionOrder,
    hiddenSectionIds,
  };
}

export function areScoutDashboardSectionConfigsEqual(
  firstConfig: ScoutDashboardSectionConfig,
  secondConfig: ScoutDashboardSectionConfig
): boolean {
  if (firstConfig.sectionOrder.length !== secondConfig.sectionOrder.length) {
    return false;
  }

  if (firstConfig.hiddenSectionIds.length !== secondConfig.hiddenSectionIds.length) {
    return false;
  }

  return (
    firstConfig.sectionOrder.every(
      (sectionId, index) => secondConfig.sectionOrder[index] === sectionId
    ) &&
    firstConfig.hiddenSectionIds.every(
      (sectionId, index) => secondConfig.hiddenSectionIds[index] === sectionId
    )
  );
}

function areSectionOrdersEqual(firstOrder: string[], secondOrder: string[]): boolean {
  return (
    firstOrder.length === secondOrder.length &&
    firstOrder.every((sectionId, index) => secondOrder[index] === sectionId)
  );
}

/**
 * Normalizes the current and saved section configurations against the live section
 * set, and clears the active saved configuration when the current layout no
 * longer matches it.
 */
export function normalizeScoutDashboardSectionSettings(
  settings: ScoutDashboardSectionSettings,
  sections: ScoutDashboardSectionOption[]
): ScoutDashboardNormalizedSectionSettings {
  const current = normalizeScoutDashboardSectionConfig(settings.current, sections);
  const savedConfigs: Record<string, ScoutDashboardSectionConfig> = {};

  Object.entries(settings.savedConfigs).forEach(([name, config]) => {
    savedConfigs[name] = normalizeScoutDashboardSectionConfig(config, sections);
  });

  const activeConfig =
    settings.activeConfigName === null ? undefined : savedConfigs[settings.activeConfigName];
  const activeConfigName =
    settings.activeConfigName !== null &&
    activeConfig &&
    areScoutDashboardSectionConfigsEqual(current, activeConfig)
      ? settings.activeConfigName
      : null;

  return {
    current,
    savedConfigs,
    activeConfigName,
  };
}

export function setCurrentScoutDashboardSectionConfig(
  settings: ScoutDashboardSectionSettings,
  nextConfig: ScoutDashboardSectionConfig,
  sections: ScoutDashboardSectionOption[]
): ScoutDashboardSectionSettings {
  return normalizeScoutDashboardSectionSettings(
    {
      ...settings,
      current: nextConfig,
    },
    sections
  );
}

export function saveScoutDashboardSectionConfig(
  settings: ScoutDashboardSectionSettings,
  name: string,
  sections: ScoutDashboardSectionOption[]
): ScoutDashboardSectionSettings {
  const normalizedCurrent = normalizeScoutDashboardSectionConfig(settings.current, sections);
  return normalizeScoutDashboardSectionSettings(
    {
      ...settings,
      current: normalizedCurrent,
      savedConfigs: {
        ...settings.savedConfigs,
        [name]: normalizedCurrent,
      },
      activeConfigName: name,
    },
    sections
  );
}

export function deleteScoutDashboardSectionConfig(
  settings: ScoutDashboardSectionSettings,
  name: string,
  sections: ScoutDashboardSectionOption[]
): ScoutDashboardSectionSettings {
  const savedConfigs = { ...settings.savedConfigs };
  delete savedConfigs[name];

  return normalizeScoutDashboardSectionSettings(
    {
      ...settings,
      savedConfigs,
      activeConfigName: settings.activeConfigName === name ? null : settings.activeConfigName,
    },
    sections
  );
}

export function selectScoutDashboardSectionConfig(
  settings: ScoutDashboardSectionSettings,
  name: string,
  sections: ScoutDashboardSectionOption[]
): ScoutDashboardSectionSettings {
  const selectedConfig = settings.savedConfigs[name];
  if (!selectedConfig) {
    return normalizeScoutDashboardSectionSettings(settings, sections);
  }

  const current = normalizeScoutDashboardSectionConfig(selectedConfig, sections);
  return normalizeScoutDashboardSectionSettings(
    {
      ...settings,
      current,
      activeConfigName: name,
    },
    sections
  );
}

export function reorderScoutDashboardSectionIds(
  sectionOrder: string[],
  draggedSectionId: string,
  targetSectionId: string
): string[] {
  const oldIndex = sectionOrder.indexOf(draggedSectionId);
  const targetIndex = sectionOrder.indexOf(targetSectionId);

  if (oldIndex === -1 || targetIndex === -1 || oldIndex === targetIndex) {
    return [...sectionOrder];
  }

  const nextOrder = sectionOrder.filter((sectionId) => sectionId !== draggedSectionId);
  const insertIndex = Math.min(targetIndex, nextOrder.length);
  nextOrder.splice(insertIndex, 0, draggedSectionId);
  return nextOrder;
}

export function getVisibleScoutDashboardSectionItems<TItem extends { id: string }>(
  items: TItem[],
  config: ScoutDashboardSectionConfig
): TItem[] {
  const itemsById = new Map(items.map((item) => [item.id, item]));
  const hiddenSectionIdSet = new Set(config.hiddenSectionIds);
  const visibleItems: TItem[] = [];

  for (const sectionId of config.sectionOrder) {
    const item = itemsById.get(sectionId);
    if (item && !hiddenSectionIdSet.has(sectionId)) {
      visibleItems.push(item);
    }
  }

  return visibleItems;
}

export function ManageScoutChartsPopover({
  open,
  anchorEl,
  onClose,
  sections,
  currentConfig,
  savedConfigs,
  activeConfigName,
  onCurrentConfigChange,
  onSaveConfig,
  onDeleteConfig,
  onSelectConfig,
  onResetConfig,
}: {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  sections: ScoutDashboardSectionOption[];
  currentConfig: ScoutDashboardSectionConfig;
  savedConfigs: Record<string, ScoutDashboardSectionConfig | LegacyScoutDashboardChartConfig>;
  activeConfigName: string | null;
  onCurrentConfigChange: (config: ScoutDashboardSectionConfig) => void;
  onSaveConfig: (name: string) => void;
  onDeleteConfig: (name: string) => void;
  onSelectConfig: (name: string) => void;
  onResetConfig: () => void;
}) {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [saveAsConfig, setSaveAsConfig] = useState(false);
  const [configName, setConfigName] = useState("");
  const [expandSavedConfigs, setExpandSavedConfigs] = useState(false);
  const [hoveredSectionId, setHoveredSectionId] = useState<string | null>(null);
  const [draggingSectionId, setDraggingSectionId] = useState<string | null>(null);
  const [dragPreviewSectionOrder, setDragPreviewSectionOrder] = useState<string[] | null>(null);
  const normalizedConfig = useMemo(
    () => normalizeScoutDashboardSectionConfig(currentConfig, sections),
    [currentConfig, sections]
  );
  const sectionById = useMemo(
    () => new Map(sections.map((section) => [section.id, section])),
    [sections]
  );
  const displayedSectionOrder = dragPreviewSectionOrder ?? normalizedConfig.sectionOrder;
  const orderedSections = useMemo(() => {
    const nextSections: ScoutDashboardSectionOption[] = [];

    for (const sectionId of displayedSectionOrder) {
      const section = sectionById.get(sectionId);
      if (section) {
        nextSections.push(section);
      }
    }

    return nextSections;
  }, [displayedSectionOrder, sectionById]);
  const filteredSections = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();
    if (!normalizedSearch) {
      return orderedSections;
    }

    return orderedSections.filter((section) =>
      [
        section.title,
        section.id,
        ...section.charts.map((chart) => chart.title),
        ...section.charts.map((chart) => chart.id),
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch)
    );
  }, [orderedSections, searchQuery]);
  const hiddenSectionIdSet = useMemo(
    () => new Set(normalizedConfig.hiddenSectionIds),
    [normalizedConfig.hiddenSectionIds]
  );
  const isSearchActive = searchQuery.trim().length > 0;
  const allSelected = filteredSections.every((section) => !hiddenSectionIdSet.has(section.id));
  const hasVisibleFilteredSection = filteredSections.some(
    (section) => !hiddenSectionIdSet.has(section.id)
  );
  const hasHiddenFilteredSection = filteredSections.some((section) =>
    hiddenSectionIdSet.has(section.id)
  );
  const indeterminate = hasVisibleFilteredSection && hasHiddenFilteredSection;
  const savedConfigNames = useMemo(() => Object.keys(savedConfigs).sort(), [savedConfigs]);
  const trimmedConfigName = configName.trim();
  const isDuplicateConfigName =
    trimmedConfigName.length > 0 && Boolean(savedConfigs[trimmedConfigName]);
  const savedConfigChipColor = theme.palette.encampBlue?.main ?? theme.palette.primary.main;
  const defaultConfig = useMemo(
    () => createDefaultScoutDashboardSectionConfig(sections.map((section) => section.id)),
    [sections]
  );

  const updateHiddenSectionIds = useCallback(
    (hiddenSectionIds: string[]) => {
      onCurrentConfigChange({
        sectionOrder: normalizedConfig.sectionOrder,
        hiddenSectionIds,
      });
    },
    [normalizedConfig.sectionOrder, onCurrentConfigChange]
  );

  const handleToggleSection = useCallback(
    (sectionId: string) => {
      const nextHiddenSectionIdSet = new Set(normalizedConfig.hiddenSectionIds);
      if (nextHiddenSectionIdSet.has(sectionId)) {
        nextHiddenSectionIdSet.delete(sectionId);
      } else {
        nextHiddenSectionIdSet.add(sectionId);
      }

      updateHiddenSectionIds(
        normalizedConfig.sectionOrder.filter((orderedSectionId) =>
          nextHiddenSectionIdSet.has(orderedSectionId)
        )
      );
    },
    [normalizedConfig.hiddenSectionIds, normalizedConfig.sectionOrder, updateHiddenSectionIds]
  );

  const handleToggleAll = useCallback(() => {
    const filteredSectionIds = new Set(filteredSections.map((section) => section.id));
    const nextHiddenSectionIdSet = new Set(normalizedConfig.hiddenSectionIds);
    const shouldShowFilteredSections = !allSelected;

    filteredSectionIds.forEach((sectionId) => {
      if (shouldShowFilteredSections) {
        nextHiddenSectionIdSet.delete(sectionId);
      } else {
        nextHiddenSectionIdSet.add(sectionId);
      }
    });

    updateHiddenSectionIds(
      normalizedConfig.sectionOrder.filter((sectionId) => nextHiddenSectionIdSet.has(sectionId))
    );
  }, [
    allSelected,
    filteredSections,
    normalizedConfig.hiddenSectionIds,
    normalizedConfig.sectionOrder,
    updateHiddenSectionIds,
  ]);

  const handleDragStart = useCallback(
    (event: DragEvent<HTMLButtonElement>, sectionId: string) => {
      if (isSearchActive) {
        event.preventDefault();
        return;
      }

      setDraggingSectionId(sectionId);
      setHoveredSectionId(null);
      setDragPreviewSectionOrder(normalizedConfig.sectionOrder);
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", sectionId);
    },
    [isSearchActive, normalizedConfig.sectionOrder]
  );

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!draggingSectionId || isSearchActive) {
        return;
      }

      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
    },
    [draggingSectionId, isSearchActive]
  );

  const moveDraggedSection = useCallback(
    (targetSectionId: string) => {
      if (!draggingSectionId || isSearchActive || draggingSectionId === targetSectionId) {
        return;
      }

      setHoveredSectionId(targetSectionId);
      const sectionOrderToReorder = dragPreviewSectionOrder ?? normalizedConfig.sectionOrder;
      const sectionOrder = reorderScoutDashboardSectionIds(
        sectionOrderToReorder,
        draggingSectionId,
        targetSectionId
      );

      setDragPreviewSectionOrder(sectionOrder);
    },
    [dragPreviewSectionOrder, draggingSectionId, isSearchActive, normalizedConfig.sectionOrder]
  );

  const handleDrop = useCallback((event: DragEvent<HTMLElement>) => {
    event.preventDefault();
  }, []);

  const handleDragEnd = useCallback(() => {
    const hasSectionOrderChanged =
      dragPreviewSectionOrder &&
      !areSectionOrdersEqual(dragPreviewSectionOrder, normalizedConfig.sectionOrder);

    setDraggingSectionId(null);

    if (hasSectionOrderChanged) {
      setHoveredSectionId(draggingSectionId);
      onCurrentConfigChange({
        sectionOrder: dragPreviewSectionOrder,
        hiddenSectionIds: normalizedConfig.hiddenSectionIds,
      });
      setDragPreviewSectionOrder(null);
      return;
    }

    setHoveredSectionId(null);
    setDragPreviewSectionOrder(null);
  }, [
    dragPreviewSectionOrder,
    draggingSectionId,
    normalizedConfig.hiddenSectionIds,
    normalizedConfig.sectionOrder,
    onCurrentConfigChange,
  ]);

  const handleSaveConfig = useCallback(() => {
    if (!trimmedConfigName) {
      return;
    }

    onSaveConfig(trimmedConfigName);
    setSaveAsConfig(false);
    setConfigName("");
    setExpandSavedConfigs(true);
  }, [onSaveConfig, trimmedConfigName]);

  const handleClose = useCallback(() => {
    setSaveAsConfig(false);
    setConfigName("");
    setHoveredSectionId(null);
    setDraggingSectionId(null);
    setDragPreviewSectionOrder(null);
    onClose();
  }, [onClose]);

  const isDefaultConfig = areScoutDashboardSectionConfigsEqual(normalizedConfig, defaultConfig);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: 360,
          maxHeight: 560,
        }}
      >
        <Box sx={{ px: 1, pt: 1, pb: 0.5, borderBottom: 1, borderColor: "divider" }}>
          <TextField
            size="small"
            placeholder="Search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery("")} edge="end">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={{ px: 2, pt: 1.5, pb: 1, flexShrink: 0 }}>
          <Chip
            label="Saved configurations"
            onClick={() => setExpandSavedConfigs(!expandSavedConfigs)}
            onDelete={() => setExpandSavedConfigs(!expandSavedConfigs)}
            deleteIcon={expandSavedConfigs ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              backgroundColor: savedConfigNames.length > 0 ? savedConfigChipColor : undefined,
              color: savedConfigNames.length > 0 ? "white" : undefined,
              height: 28,
              "& .MuiChip-deleteIcon": {
                color: savedConfigNames.length > 0 ? "white" : theme.palette.text.secondary,
              },
              "&:hover": {
                backgroundColor: savedConfigNames.length > 0 ? "#4568B0" : undefined,
              },
            }}
          />

          <Collapse in={expandSavedConfigs}>
            <Paper
              variant="outlined"
              sx={{
                mt: 1.5,
                maxHeight: 136,
                overflow: "auto",
                backgroundColor: "white",
              }}
            >
              {savedConfigNames.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 2, textAlign: "center" }}
                >
                  No saved configurations yet
                </Typography>
              ) : (
                <Box>
                  {savedConfigNames.map((name) => (
                    <Box
                      key={name}
                      onClick={() => onSelectConfig(name)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        minHeight: 34,
                        cursor: "pointer",
                        backgroundColor:
                          activeConfigName === name ? "action.selected" : "transparent",
                        borderBottom: 1,
                        borderColor: "divider",
                        "&:last-child": {
                          borderBottom: 0,
                        },
                        "&:hover": {
                          backgroundColor:
                            activeConfigName === name ? "action.selected" : "action.hover",
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          px: 1,
                          py: 0.5,
                        }}
                      >
                        {name}
                      </Typography>
                      <Box
                        sx={{
                          width: 44,
                          flexShrink: 0,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <IconButton
                          size="small"
                          aria-label={`Delete ${name} chart configuration`}
                          onClick={(event) => {
                            event.stopPropagation();
                            onDeleteConfig(name);
                          }}
                          sx={{ padding: "5px" }}
                        >
                          <RemoveIcon
                            sx={{
                              fontSize: 20,
                              color: "action.disabled",
                            }}
                          />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Collapse>
        </Box>

        <Box sx={{ flex: 1, overflow: "auto" }}>
          {searchQuery.trim() && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ px: 2, py: 0.5, display: "block" }}
            >
              Showing {filteredSections.length} of {orderedSections.length} sections
            </Typography>
          )}
          {filteredSections.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 2, py: 1, display: "block" }}
            >
              No sections match your search
            </Typography>
          ) : (
            <List onDragOver={handleDragOver} onDrop={handleDrop} sx={{ py: 0 }}>
              {filteredSections.map((section) => (
                <ListItem
                  key={section.id}
                  onMouseEnter={() => setHoveredSectionId(section.id)}
                  onMouseLeave={() =>
                    setHoveredSectionId((previousHoveredSectionId) =>
                      previousHoveredSectionId === section.id ? null : previousHoveredSectionId
                    )
                  }
                  onDragEnter={() => moveDraggedSection(section.id)}
                  onDragOver={handleDragOver}
                  sx={{
                    p: 0,
                    pl: 0.5,
                    pr: 1,
                    opacity: draggingSectionId === section.id ? 0.55 : 1,
                    backgroundColor:
                      !draggingSectionId && hoveredSectionId === section.id
                        ? "action.hover"
                        : "transparent",
                    transition: "background-color 120ms ease, opacity 120ms ease",
                  }}
                >
                  <IconButton
                    size="small"
                    aria-label={`Reorder ${section.title}`}
                    disabled={isSearchActive}
                    draggable={!isSearchActive}
                    onDragStart={(event) => handleDragStart(event, section.id)}
                    onDragEnd={handleDragEnd}
                    sx={{
                      cursor: isSearchActive ? "default" : "grab",
                      color: "text.secondary",
                    }}
                  >
                    <DragIndicator fontSize="small" />
                  </IconButton>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!hiddenSectionIdSet.has(section.id)}
                        onChange={() => handleToggleSection(section.id)}
                        size="small"
                      />
                    }
                    label={
                      <Box minWidth={0}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                          sx={{ lineHeight: 1.25 }}
                        >
                          {section.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {section.charts.map((chart) => chart.title).join(", ")}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      width: "100%",
                      margin: 0,
                      minWidth: 0,
                      py: 0.5,
                      "& .MuiFormControlLabel-label": {
                        minWidth: 0,
                        width: "100%",
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        <Stack
          spacing={1}
          sx={{
            p: 1,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          {saveAsConfig && (
            <TextField
              fullWidth
              label="Configuration name"
              value={configName}
              onChange={(event) => setConfigName(event.target.value)}
              size="small"
              placeholder="Enter configuration name"
              autoFocus
              helperText={
                isDuplicateConfigName
                  ? "A configuration with this name already exists. Saving will overwrite it."
                  : undefined
              }
            />
          )}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={indeterminate}
                  onChange={handleToggleAll}
                  size="small"
                />
              }
              label="Show/Hide All"
              sx={{
                margin: 0,
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.875rem",
                  color: "text.secondary",
                },
              }}
            />
            <Button disabled={isDefaultConfig} onClick={onResetConfig} size="small">
              Reset
            </Button>
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Tooltip
              title="Saved chart configurations are stored in your browser. Clearing browser data or using a different device/browser will remove them."
              placement="top"
              arrow
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={saveAsConfig}
                    onChange={(event) => setSaveAsConfig(event.target.checked)}
                    size="small"
                  />
                }
                label="Save as configuration"
                sx={{
                  margin: 0,
                  "& .MuiFormControlLabel-label": {
                    fontSize: "0.875rem",
                    color: "text.secondary",
                  },
                }}
              />
            </Tooltip>
            <Button
              variant="contained"
              size="small"
              disabled={!saveAsConfig || !trimmedConfigName}
              onClick={handleSaveConfig}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Popover>
  );
}
